import React, { useCallback } from 'react';

import {
  IncidentMedicalAidStation,
  useIncidentMedicalAidStations,
  useCreateIncidentMedicalAidStation,
  useEditIncidentMedicalAidStation,
} from 'components/IAP/hooks/incidentMedicalAidStationsHooks';
import { MedicalAidStationBase } from 'types/iapBaseTypes';

import {
  MedicalPlanTable,
  type MedicalPlanColumn,
  type MedicalPlanDraft,
} from './MedicalPlanTable';
import { useRecordMedicalPlanVersion } from './medicalPlanVersionRecording';

const COLUMNS: MedicalPlanColumn[] = [
  {
    kind: 'text',
    field: 'name',
    header: 'Name',
    placeholder: 'Name *',
    maxLength: 200,
  },
  {
    kind: 'text',
    field: 'location',
    header: 'Location',
    placeholder: 'Location',
  },
  {
    kind: 'text',
    field: 'contact_number',
    header: 'Contact Information',
    placeholder: 'Contact',
    maxLength: 40,
  },
  {
    kind: 'checkbox',
    field: 'paramedics_on_site',
    header: 'Paramedics On site?',
  },
];

const EMPTY_DRAFT: MedicalPlanDraft = {
  name: '',
  location: '',
  contact_number: '',
  paramedics_on_site: false,
};

const toEditPayload = (
  entity: IncidentMedicalAidStation,
  override: MedicalPlanDraft
): MedicalAidStationBase & {
  id: string;
  medical_aid_station_id: string;
  archived: boolean;
} => ({
  id: entity.id,
  medical_aid_station_id: entity.medical_aid_station_id,
  name: entity.name,
  description: entity.description,
  location: entity.location,
  contact_number: entity.contact_number,
  paramedics_on_site: entity.paramedics_on_site,
  archived: entity.archived,
  ...override,
});

type Props = { readOnly?: boolean };

export const MedicalAidStationsTable: React.FC<Props> = ({
  readOnly = false,
}) => {
  const { data: medicalAidStations } =
    useIncidentMedicalAidStations('incident');
  const activeMedicalAidStations = (medicalAidStations ?? []).filter(
    (e) => !e.archived
  );

  const recordVersion = useRecordMedicalPlanVersion();
  const { mutateAsync: createRecord, isPending: isCreating } =
    useCreateIncidentMedicalAidStation();
  const { mutateAsync: editRecord, isPending: isEditing } =
    useEditIncidentMedicalAidStation();

  const onCreate = useCallback(
    async (draft: MedicalPlanDraft) => {
      await createRecord({
        name: draft.name as string,
        location: draft.location as string,
        contact_number: draft.contact_number as string,
        paramedics_on_site: draft.paramedics_on_site as boolean,
      });
      recordVersion();
    },
    [createRecord, recordVersion]
  );

  const onUpdate = useCallback(
    async (entity: IncidentMedicalAidStation, draft: MedicalPlanDraft) => {
      await editRecord(toEditPayload(entity, draft));
      recordVersion();
    },
    [editRecord, recordVersion]
  );

  const onDelete = useCallback(
    async (entity: IncidentMedicalAidStation) => {
      await editRecord(toEditPayload(entity, { archived: true }));
      recordVersion();
    },
    [editRecord, recordVersion]
  );

  return (
    <MedicalPlanTable
      testId="ics206-aid-stations"
      entityNoun="Aid station"
      entityNounPlural="Aid stations"
      addButtonLabel="Add Station"
      noDataMessage="No medical aid stations defined."
      entities={activeMedicalAidStations}
      columns={COLUMNS}
      emptyDraft={EMPTY_DRAFT}
      requiredField="name"
      rowLabelField="name"
      isSaving={isCreating || isEditing}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onDelete={onDelete}
      readOnly={readOnly}
    />
  );
};
